import React, { useState, useEffect, useMemo } from 'react';
import { UserProfile, CharacterTrait, Mindset, ApiKeyStatus } from '@/types';
import { DEFAULT_PLAYER_NAME, DEFAULT_PLAYER_STATS, DEFAULT_SKILLS, DEFAULT_INVENTORY, DEFAULT_AGE } from '@/constants';
import { AlertTriangleIcon, CheckCircleIcon, ImageIcon, InformationCircleIcon as InfoIcon, LoadingSpinnerIcon as LoaderIcon, ArrowDownTrayIcon as SaveIcon, SparklesIcon, UserIcon, XCircleIcon } from './icons';
import { generateImage } from '@/services/openRouterToElevenLabsAdapter';
import { useCharacterPortrait } from '@/services/pollinationsReactService';
import { fluxImageService } from '@/services/fluxImageService';
import { generateOptimizedImage } from '@/services/optimizedImageService';
import { contentModerationService } from '@/services/contentModerationService';
import { ShareButton } from './sharing';

const MAX_TRAITS = 3;
const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 20;
const MIN_AGE = 16;
const MAX_AGE = 99;

interface UserProfileScreenProps {
  onProfileComplete: (profile: UserProfile) => void;
  apiKeyStatus: ApiKeyStatus;
  traits: CharacterTrait[];
  mindsets: Mindset[];
  isLoadingAppearance: boolean;
  onSetLoadingAppearance: (isLoading: boolean) => void;
  onSetError: (error: string | null) => void;
  userProfile: UserProfile | null; 
  onUpdateProfile: (updatedProfile: Partial<UserProfile>) => void; // For partial updates like image
  currentUserId: string | null; // Current user ID
}

const UserProfileScreen: React.FC<UserProfileScreenProps> = ({ 
  onProfileComplete, 
  apiKeyStatus, 
  traits, 
  mindsets,
  isLoadingAppearance,
  onSetLoadingAppearance,
  onSetError,
  userProfile,
  onUpdateProfile,
  currentUserId // Current user ID
}) => {
  const [name, setName] = useState(userProfile?.name || DEFAULT_PLAYER_NAME);
  const [age, setAge] = useState<number | ''>(userProfile?.age || DEFAULT_AGE);
  const [selectedTraitIds, setSelectedTraitIds] = useState<string[]>(userProfile?.traits?.map(t => t.id) || []);
  const [selectedMindsetId, setSelectedMindsetId] = useState<string | null>(userProfile?.mindset?.id || null);
  const [appearanceDescription, setAppearanceDescription] = useState('');
  const [profileImage, setProfileImage] = useState<string | null>(userProfile?.appearanceImageUrl || null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [shouldGenerateImage, setShouldGenerateImage] = useState(false);
  
  // Get selected data for character portrait generation
  const selectedMindset = mindsets.find(m => m.id === selectedMindsetId);
  const selectedTraits = traits.filter(t => selectedTraitIds.includes(t.id));
  
  // Memoize stable values for hook dependencies to prevent unnecessary re-renders
  const stableTraitNames = useMemo(() => selectedTraits.map(trait => trait.name), [selectedTraits]);
  const stableMindsetName = useMemo(() => selectedMindset?.name || '', [selectedMindset?.name]);
  const stableAppearanceDescription = useMemo(() => appearanceDescription.trim(), [appearanceDescription]);
  const stableName = useMemo(() => name.trim() || DEFAULT_PLAYER_NAME, [name]);
  
  // Use Pollinations React hook for image generation (manual trigger)
  const { imageUrl: generatedImageUrl, isLoading: isGeneratingImage, error: imageGenerationError, resetGeneration } = useCharacterPortrait(
    shouldGenerateImage, // Manual trigger control
    stableName,
    stableMindsetName,
    stableTraitNames,
    stableAppearanceDescription,
    {
      width: 400,
      height: 400,
      model: 'flux'
    }
  );

  // Track if we've initialized from userProfile to avoid resetting on image updates
  // This prevents the form from resetting when the parent updates only the image URL
  const [hasInitialized, setHasInitialized] = useState(false);
  
  useEffect(() => {
    // Only initialize form fields from userProfile once, or when userProfile changes from null to a value
    // This prevents form reset when generating avatar (which only updates appearanceImageUrl)
    if (userProfile && !hasInitialized) {
      setName(userProfile.name || DEFAULT_PLAYER_NAME);
      setAge(userProfile.age || DEFAULT_AGE);
      setSelectedTraitIds(userProfile.traits?.map(t => t.id) || []);
      setSelectedMindsetId(userProfile.mindset?.id || null);
      setProfileImage(userProfile.appearanceImageUrl || null);
      setHasInitialized(true);
    }
    // Always update the profile image when it changes in userProfile
    if (userProfile?.appearanceImageUrl && userProfile.appearanceImageUrl !== profileImage) {
      setProfileImage(userProfile.appearanceImageUrl);
    }
  }, [userProfile, hasInitialized, profileImage]);

  const handleTraitToggle = (traitId: string) => {
    setLocalError(null);
    setSelectedTraitIds(prev => {
      if (prev.includes(traitId)) {
        return prev.filter(id => id !== traitId);
      }
      if (prev.length < MAX_TRAITS) {
        return [...prev, traitId];
      }
      setLocalError(`You can select a maximum of ${MAX_TRAITS} traits.`);
      return prev; 
    });
  };

  // Use effect to handle image generation results from React hook
  useEffect(() => {
    if (generatedImageUrl && generatedImageUrl !== profileImage && shouldGenerateImage) {
      console.log('[UserProfileScreen] New image generated via React hook:', generatedImageUrl);
      setProfileImage(generatedImageUrl);
      onUpdateProfile({ appearanceImageUrl: generatedImageUrl });
      setSuccessMessage("Character appearance generated successfully!");
      setLocalError(null);
      onSetError(null);
      // Reset the generation trigger after successful generation
      setShouldGenerateImage(false);
    }
    
    if (imageGenerationError && shouldGenerateImage) {
      console.error('[UserProfileScreen] Image generation error:', imageGenerationError);
      setLocalError(imageGenerationError);
      onSetError(imageGenerationError);
      // Reset the generation trigger on error
      setShouldGenerateImage(false);
    }
  }, [generatedImageUrl, imageGenerationError, profileImage, shouldGenerateImage, onUpdateProfile, onSetError]);

  // Update loading state based on React hook
  useEffect(() => {
    onSetLoadingAppearance(isGeneratingImage);
  }, [isGeneratingImage, onSetLoadingAppearance]);

  const handleGenerateAppearance = async () => {
    if (!appearanceDescription.trim()) {
      setLocalError("Please enter a detailed appearance description for best results.");
      return;
    }
    setLocalError(null);
    setSuccessMessage(null);
    onSetError(null);
    onSetLoadingAppearance(true);
    
    try {
      console.log('[UserProfileScreen] Image generation triggered manually via button click');
      
      // Build the prompt
      const characterName = name.trim() || DEFAULT_PLAYER_NAME;
      const prompt = `Portrait of ${characterName}, ${appearanceDescription || 'cyberpunk character'}, half body shot from torso up, cyberpunk fantasy style, digital art, high quality, detailed face and torso, atmospheric lighting, futuristic setting`;
      
      console.log('[UserProfileScreen] Generating with free image service, prompt:', prompt);
      
      // Use optimized image service for portraits
      const imageUrl = await generateOptimizedImage(prompt, {
        width: 400,
        height: 400,
        seed: Math.floor(Math.random() * 1000000),
        type: 'portrait'
      });
      
      console.log('[UserProfileScreen] Free service generated image:', imageUrl);
      setProfileImage(imageUrl);
      onUpdateProfile({ appearanceImageUrl: imageUrl });
      setSuccessMessage("Character appearance generated successfully!");
      
    } catch (error) {
      console.warn('[UserProfileScreen] Free image service failed, falling back to Pollinations:', error);
      // Fallback to original Pollinations method
      resetGeneration();
      setShouldGenerateImage(true);
    } finally {
      onSetLoadingAppearance(false);
    }
  };

  const validateProfile = async (): Promise<boolean> => {
    if (!name.trim() || name.trim().length < MIN_NAME_LENGTH || name.trim().length > MAX_NAME_LENGTH) {
      setLocalError(`Name must be between ${MIN_NAME_LENGTH} and ${MAX_NAME_LENGTH} characters.`);
      return false;
    }
    
    // Check if username is appropriate
    const isAppropriate = await contentModerationService.isUsernameAppropriate(name.trim());
    if (!isAppropriate) {
      setLocalError("Please choose a different name. This one may not be appropriate for the game.");
      return false;
    }
    if (age === '' || age < MIN_AGE || age > MAX_AGE) {
      setLocalError(`Age must be between ${MIN_AGE} and ${MAX_AGE}.`);
      return false;
    }
    if (selectedTraitIds.length === 0) {
      setLocalError("Please select at least one trait.");
      return false;
    }
    if (selectedTraitIds.length > MAX_TRAITS) {
        setLocalError(`You can select a maximum of ${MAX_TRAITS} traits.`);
        return false;
    }
    if (!selectedMindsetId) {
      setLocalError("Please choose a mindset.");
      return false;
    }
    // Image generation is now optional since it uses React hooks
    // if (!profileImage) {
    //     setLocalError("Please generate your character's appearance or provide a description.");
    //     return false; // Optional: make image generation mandatory
    // }
    return true;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);

    if (!validateProfile()) {
        return;
    }

    const finalTraits = traits.filter(trait => selectedTraitIds.includes(trait.id));
    const finalMindset = mindsets.find(mindset => mindset.id === selectedMindsetId) || null;
    
    const profileUid = currentUserId || userProfile?.uid;

    if (!profileUid) {
      setLocalError("Cannot save profile: User ID is missing. Please try logging in again.");
      console.error("UserProfileScreen: handleSubmit: profileUid is missing. currentUserId:", currentUserId, "userProfile?.uid:", userProfile?.uid);
      return;
    }

    const completeProfile: UserProfile = {
      uid: profileUid,
      name: name.trim(),
      age: typeof age === 'number' ? age : DEFAULT_AGE,
      traits: finalTraits,
      mindset: finalMindset,
      appearanceImageUrl: profileImage || `https://picsum.photos/seed/${name.trim() || 'default_dreamer'}/300/300`,
      stats: userProfile?.stats || DEFAULT_PLAYER_STATS,
      skills: userProfile?.skills || DEFAULT_SKILLS,
      inventory: userProfile?.inventory || DEFAULT_INVENTORY,
    };

    // Create feed post for character creation if this is a new profile
    if (!userProfile) {
      try {
        const { databaseService } = await import('@/services/databaseService');
        await databaseService.createCharacterCreationPost(completeProfile, profileImage || undefined);
        console.log('[UserProfileScreen] Created character creation feed post');
      } catch (error) {
        console.warn('[UserProfileScreen] Failed to create feed post:', error);
        // Don't fail profile creation if feed post fails
      }
    }

    onProfileComplete(completeProfile);
    setSuccessMessage("Profile saved successfully!");
  };

  const getApiKeyMessage = () => {
    switch (apiKeyStatus) {
      case 'not_exists':
        return { text: "Free mode active. All features available!", Icon: CheckCircleIcon, color: 'text-green-400', bg: 'bg-green-900/50' };
      case 'exists':
        return { text: "Premium features enabled.", Icon: CheckCircleIcon, color: 'text-green-400', bg: 'bg-green-900/50' };
      case 'checking':
        return { text: "Checking system status...", Icon: LoaderIcon, color: 'text-slate-400', bg: 'bg-slate-700/50' };
      default:
        return { text: "System status unknown...", Icon: LoaderIcon, color: 'text-slate-400', bg: 'bg-slate-700/50' };
    }
  };

  const apiKeyInfo = getApiKeyMessage();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-4 md:p-6 lg:p-8 font-sans flex flex-col items-center">
      <div className="w-full max-w-2xl bg-slate-900 shadow-2xl rounded-xl border border-slate-700/50">
        <header className="p-6 border-b border-slate-700">
          <h1 className="text-3xl md:text-4xl font-orbitron text-center text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-500 via-purple-500 to-cyan-500 neon-text-purple">
            Create Your Persona
          </h1>
          <p className="text-center text-slate-400 mt-2 text-sm">Define your digital self to venture into the Lucid Cyber RPG.</p>
        </header>

        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
          {apiKeyStatus !== 'checking' && (
            <div className={`p-3 rounded-md text-sm flex items-center ${apiKeyInfo.bg} border border-current`}>
              <apiKeyInfo.Icon className={`w-5 h-5 mr-2 flex-shrink-0 ${apiKeyInfo.color}`} />
              <span className={`${apiKeyInfo.color}`}>{apiKeyInfo.text}</span>
            </div>
          )}

          {localError && (
            <div className="p-3 bg-red-900/60 border border-red-500/70 rounded-lg text-red-200 text-sm flex items-center">
              <XCircleIcon className="w-5 h-5 mr-2 text-red-300 flex-shrink-0" /> {localError}
            </div>
          )}
          {successMessage && (
            <div className="p-3 bg-green-900/60 border border-green-500/70 rounded-lg text-green-200 text-sm flex items-center">
              <CheckCircleIcon className="w-5 h-5 mr-2 text-green-300 flex-shrink-0" /> {successMessage}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-1">Persona Name</label>
              <div className="relative">
                <UserIcon className="w-5 h-5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2"/>
                <input 
                  type="text" 
                  id="name" 
                  value={name} 
                  onChange={(e) => {setName(e.target.value); setLocalError(null);}} 
                  className="w-full pl-10 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none placeholder-slate-500"
                  placeholder="e.g., NeoDreamerX"
                  required 
                  minLength={MIN_NAME_LENGTH}
                  maxLength={MAX_NAME_LENGTH}
                />
              </div>
            </div>
            <div>
              <label htmlFor="age" className="block text-sm font-medium text-slate-300 mb-1">Age (Years)</label>
              <input 
                type="number" 
                id="age" 
                value={age} 
                onChange={(e) => {setAge(e.target.value === '' ? '' : parseInt(e.target.value)); setLocalError(null);}} 
                className="w-full p-2 bg-slate-800 border border-slate-700 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                placeholder={`e.g., ${DEFAULT_AGE}`}
                min={MIN_AGE}
                max={MAX_AGE}
                required
              />
            </div>
          </div>

          {/* Appearance Section */} 
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/70">
            <h3 className="text-lg font-orbitron text-slate-100 mb-3 flex items-center"><ImageIcon className="w-5 h-5 mr-2 text-purple-400"/>Visual Appearance</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div>
                    <label htmlFor="appearanceDescription" className="block text-sm font-medium text-slate-300 mb-1">Describe Your Look</label>
                    <textarea 
                        id="appearanceDescription" 
                        value={appearanceDescription} 
                        onChange={(e) => {setAppearanceDescription(e.target.value); setLocalError(null);}} 
                        rows={4} 
                        className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md focus:ring-1 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none placeholder-slate-500 text-sm"
                        placeholder="e.g., Cybernetic eyes, neon pink hair, trench coat with glowing runes..."
                    />
                    <button
                        type="button"
                        onClick={handleGenerateAppearance}
                        disabled={isLoadingAppearance || !appearanceDescription.trim()}
                        className="w-full mt-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white text-sm font-semibold rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center shadow-md hover:shadow-lg"
                    >
                        {isLoadingAppearance ? <LoaderIcon className="w-5 h-5 mr-2 animate-spin"/> : <SparklesIcon className="w-5 h-5 mr-2" />}
                        {isLoadingAppearance ? 'Generating...' : (profileImage ? 'Regenerate Image' : 'Generate Image')}
                    </button>
                    <p className="text-xs text-green-400 mt-1">✅ Free AI image generation - No API key required!</p>
                </div>
                <div className="flex flex-col items-center justify-center bg-slate-700/50 p-3 rounded-md border border-slate-600 min-h-[180px]">
                    {isLoadingAppearance ? (
                        <div className="flex flex-col items-center justify-center h-full">
                            <LoaderIcon className="w-12 h-12 text-purple-400 animate-spin" />
                            <p className="mt-2 text-sm text-slate-400">Crafting your avatar...</p>
                        </div>
                    ) : profileImage ? (
                        <img 
                            src={profileImage} 
                            alt="Generated character appearance" 
                            className="w-full h-auto max-h-48 object-cover rounded-md border-2 border-purple-500 shadow-lg"
                            onError={(e) => {
                                e.currentTarget.src = `https://picsum.photos/seed/${name || 'default_dreamer'}/300/300`;
                                e.currentTarget.alt = "Fallback appearance image";
                                setLocalError("Failed to load appearance image, showing fallback.");
                            }}
                        />
                    ) : (
                        <div className="text-center text-slate-500">
                            <ImageIcon className="w-16 h-16 mx-auto mb-2" />
                            <p className="text-sm">Your generated image will appear here.</p>
                        </div>
                    )}
                </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-orbitron text-slate-100 mb-2">Select Traits (Up to {MAX_TRAITS})</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {traits.map(trait => (
                <button
                  type="button"
                  key={trait.id}
                  onClick={() => handleTraitToggle(trait.id)}
                  className={`p-3 rounded-md text-sm transition-all duration-200 border-2 text-center
                    ${selectedTraitIds.includes(trait.id)
                      ? 'bg-fuchsia-600 border-fuchsia-400 text-white ring-2 ring-fuchsia-300 shadow-fuchsia-500/50 shadow-md'
                      : 'bg-slate-700 border-slate-600 hover:bg-slate-600 hover:border-fuchsia-500 text-slate-200'
                    }`}
                >
                  {trait.name}
                </button>
              ))}
            </div>
            {selectedTraitIds.length > 0 && (
              <p className="text-xs text-slate-400 mt-2">Selected: {selectedTraitIds.map(id => traits.find(t=>t.id===id)?.name).join(', ')}</p>
            )}
          </div>
          
          <div>
            <h3 className="text-lg font-orbitron text-slate-100 mb-2">Choose Mindset (Select 1)</h3>
            <div className="space-y-3">
              {mindsets.map(mindset => (
                <button
                  type="button"
                  key={mindset.id}
                  onClick={() => {setSelectedMindsetId(mindset.id); setLocalError(null);}}
                  className={`w-full text-left p-3 rounded-md transition-all duration-200 border-2
                    ${selectedMindsetId === mindset.id
                      ? 'bg-cyan-600 border-cyan-400 text-white ring-2 ring-cyan-300 shadow-cyan-500/50 shadow-md'
                      : 'bg-slate-700 border-slate-600 hover:bg-slate-600 hover:border-cyan-500 text-slate-200'
                    }`}
                >
                  <h4 className="font-semibold text-base">{mindset.name}</h4>
                  <p className="text-xs opacity-80 mt-1">{mindset.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <button
              type="submit"
              className="w-full px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-lg rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-400 shadow-xl disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
              disabled={isLoadingAppearance}
            >
              <SaveIcon className="w-6 h-6 mr-2"/>
              {userProfile ? 'Save Changes' : 'Enter the Dreamscape'}
            </button>
            
            {/* Share Character Button - Only show if profile exists */}
            {userProfile && (
              <div className="flex justify-center">
                <ShareButton
                  shareType="character"
                  shareData={{}}
                  currentUser={userProfile}
                  variant="secondary"
                  size="md"
                  text="Share Character"
                />
              </div>
            )}
          </div>
        </form>
      </div>
       <footer className="mt-8 text-center text-slate-600 text-sm">
        <p>&copy; {new Date().getFullYear()} Lucid Cyber Systems. Your reality awaits.</p>
      </footer>
    </div>
  );
};

export default UserProfileScreen;
